import { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
} from '@mui/material';
import type { MessageEvent } from '@k-ucap/shared';

const MAX_ROWS = 500;

interface MessageStreamPanelProps {
  events: MessageEvent[];
  selectedMessageId: string | null;
  onSelectMessage: (event: MessageEvent) => void;
}

const severityColor = (severity?: string): 'success' | 'warning' | 'error' | 'default' => {
  if (severity === 'FAIL') return 'error';
  if (severity === 'WARN') return 'warning';
  if (severity === 'PASS') return 'success';
  return 'default';
};

export const MessageStreamPanel = ({ events, selectedMessageId, onSelectMessage }: MessageStreamPanelProps) => {
  const rows = useMemo(() => {
    const sorted = [...events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return sorted.slice(0, MAX_ROWS);
  }, [events]);

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 2 }} data-testid="message-stream-panel">
        <Typography variant="body2" color="text.secondary">
          실행된 메시지가 없습니다. Run을 시작하면 메시지 스트림이 표시됩니다.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 360, overflow: 'auto' }} data-testid="message-stream-panel">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Time</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Source → Target</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Vehicle</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((event) => {
            const vehicleId = (event.fields?.vehicleId ?? event.payload?.vehicleId) as string | undefined;
            return (
              <TableRow
                key={event.id}
                hover
                selected={selectedMessageId === event.id}
                onClick={() => onSelectMessage(event)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {new Date(event.timestamp).toLocaleTimeString()}
                </TableCell>
                <TableCell>#{event.messageType}</TableCell>
                <TableCell>{event.name}</TableCell>
                <TableCell>
                  {event.source} → {event.target}
                </TableCell>
                <TableCell>
                  <Chip
                    label={event.validationStatus ?? event.validation ?? 'N/A'}
                    size="small"
                    color={severityColor(event.validationStatus ?? event.validation)}
                  />
                </TableCell>
                <TableCell>{vehicleId ?? '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
